import React from "react";
import Modal from "@via/components/modal.tsx";
import { useForm } from "react-hook-form";
import { ModalSize, WORKFLOW_STATUS_OPTIONS } from "@via/utils/constants.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { LinkInputGroup } from "@via/components/link-input-group/link-input-group.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect.tsx";

interface PipelineDetails {
  workflowStatus: string;
  origin: string;
  tutorialLinks: { label: string; url: string }[];
  sourceLinks: { label: string; url: string }[];
  exampleRunLinks: { label: string; url: string }[];
}

interface EditPipelineDetailsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const defaultLink = { label: "", url: "" };

type Link = { label: string; url: string };

const isLinkArray = (value: unknown): value is Link[] => {
  return (
    Array.isArray(value) &&
    value.every(
      item =>
        typeof item === "object" &&
        item !== null &&
        "label" in item &&
        "url" in item
    )
  );
};

export const EditPipelineDetails: React.FC<EditPipelineDetailsProps> = ({
  open,
  setOpen,
}) => {
  const { increaseChangeCount } = useChangesDetect();
  const { pipelineDraft, setPipelineDraft } = useGlobal();
  const status = pipelineDraft?.workflowStatus;
  const origin = pipelineDraft?.origin;

  const tutorialLinksValue = pipelineDraft?.details?.tutorialLinks;
  const tutorialLinks =
    isLinkArray(tutorialLinksValue) && tutorialLinksValue.length > 0
      ? tutorialLinksValue
      : [defaultLink];
  const sourceLinksValue = pipelineDraft?.details?.sourceLinks;
  const sourceLinks =
    isLinkArray(sourceLinksValue) && sourceLinksValue.length > 0
      ? sourceLinksValue
      : [defaultLink];
  const exampleRunLinksValue = pipelineDraft?.details?.exampleRunLinks;
  const exampleRunLinks =
    isLinkArray(exampleRunLinksValue) && exampleRunLinksValue.length > 0
      ? exampleRunLinksValue
      : [defaultLink];

  const { control, handleSubmit } = useForm<PipelineDetails>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      workflowStatus: status || undefined,
      origin: origin || "",
      tutorialLinks: tutorialLinks,
      sourceLinks: sourceLinks,
      exampleRunLinks: exampleRunLinks,
    },
  });

  const handleEditPipelineDetailsFormSubmit = (
    formPayload: PipelineDetails
  ) => {
    const { tutorialLinks, sourceLinks, exampleRunLinks, ...rest } =
      formPayload;
    const updatePipelinePayload: any = {
      ...rest,
    };

    // Filter out empty links (where both label and url are empty)
    const filteredTutorialLinks = tutorialLinks?.filter(
      link => link.label?.trim() || link.url?.trim()
    );
    const filteredSourceLinks = sourceLinks?.filter(
      link => link.label?.trim() || link.url?.trim()
    );
    const filteredExampleRunLinks = exampleRunLinks?.filter(
      link => link.label?.trim() || link.url?.trim()
    );

    if (filteredTutorialLinks && filteredTutorialLinks.length > 0) {
      updatePipelinePayload.details = {
        ...updatePipelinePayload.details,
        tutorialLinks: filteredTutorialLinks,
      };
    }

    if (filteredSourceLinks && filteredSourceLinks.length > 0) {
      updatePipelinePayload.details = {
        ...updatePipelinePayload.details,
        sourceLinks: filteredSourceLinks,
      };
    }

    if (filteredExampleRunLinks && filteredExampleRunLinks.length > 0) {
      updatePipelinePayload.details = {
        ...updatePipelinePayload.details,
        exampleRunLinks: filteredExampleRunLinks,
      };
    }

    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      ...updatePipelinePayload,
    }));
  };

  // Modal closes on true. The form has no required fields, so false is only reachable if one
  // is added later, and then it keeps the dialog open on the error.
  const handleConfirm = async (): Promise<boolean> => {
    let saved = false;
    await handleSubmit(formPayload => {
      handleEditPipelineDetailsFormSubmit(formPayload);
      saved = true;
    })();
    return saved;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Edit Pipeline Details"
      confirmText="Confirm"
      size={ModalSize.Large}
      onConfirm={handleConfirm}>
      <form
        className="flex h-full flex-col mt-6 mb-8"
        onSubmit={e => e.preventDefault()}>
        <div className="flex flex-1 flex-col gap-6 justify-between">
          <div>
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Workflow Status
            </label>
            <SelectComponent
              control={control}
              name="workflowStatus"
              options={WORKFLOW_STATUS_OPTIONS}
              placeholder="Select Workflow Status"
            />
          </div>
          <div className="-mt-2">
            <FormInputComponent
              control={control}
              name="origin"
              label="Origin"
              placeholder="Enter Origin"
            />
          </div>
          <LinkInputGroup
            control={control}
            baseName="tutorialLinks"
            title="Tutorial Link"
            maxLinks={10}
          />
          <div className="mt-2">
            <LinkInputGroup
              control={control}
              baseName="sourceLinks"
              title="Source Link"
              maxLinks={10}
            />
          </div>
          <div className="mt-2">
            <LinkInputGroup
              control={control}
              baseName="exampleRunLinks"
              title="Example Run Link"
              maxLinks={10}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
